import React, { useMemo, useState } from 'react';
import styles from './Calendar.module.css';

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const Calendar = ({
    selectedDates = [],
    onDateSelect,
    reservedDatesMap = new Map(),
    onShowDetails,
    isLoading = false,
    viewingReservation,
    currentDisplayDate,
    onMonthChange,
    isReadOnly = false,
    selectionMode = 'free',
    blocks = []
}) => {
    const [hoveredBlockId, setHoveredBlockId] = useState(null);

    const displayDate = currentDisplayDate instanceof Date ? currentDisplayDate : new Date();
    const currentYear = displayDate.getFullYear();
    const currentMonth = displayDate.getMonth(); // 0-11

    const daysInMonthGrid = useMemo(() => {
        const days = [];
        const date = new Date(currentYear, currentMonth, 1);
        const firstDayOfMonth = date.getDay();
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push({ key: `empty-${i}`, type: 'empty' });
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dayDate = new Date(currentYear, currentMonth, day);
            const dateString = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            let type = 'available';
            let isSelected = !isReadOnly && selectedDates.includes(dateString);
            let reservationDetails = null;
            let isBeingViewed = false;
            let status = null;
            let blockData = null;

            if (dayDate < today && !reservedDatesMap.has(dateString)) {
                type = 'past';
                // Only allow selection of past days if they were already included (e.g. by an Admin override)
                if (!selectedDates.includes(dateString)) {
                    isSelected = false;
                }
            } else if (reservedDatesMap.has(dateString)) {
                const details = reservedDatesMap.get(dateString);
                type = details.type || 'available';
                status = details.status;
                reservationDetails = details;
                // isSelected remains true if it's in the selectedDates array

                if (viewingReservation && details?.id === viewingReservation.id && !isReadOnly) {
                    isBeingViewed = true;
                }
            }

            // Check if day is part of a block
            if (blocks.length > 0) {
                const foundBlock = blocks.find(b => dateString >= b.startDate && dateString <= b.endDate);
                if (foundBlock) {
                    blockData = foundBlock;
                }
            }

            days.push({
                key: dateString, dayNumber: day, date: dayDate, dateString: dateString,
                type: type,
                status: status,
                isSelected: isSelected,
                reservationDetails: reservationDetails,
                isBeingViewed: isBeingViewed,
                blockData: blockData
            });
        }
        return days;
    }, [currentMonth, currentYear, selectedDates, reservedDatesMap, viewingReservation, isReadOnly, blocks]);

    const handlePrevMonth = () => {
        if (typeof onMonthChange === 'function') {
            const prevMonthDate = new Date(displayDate.getFullYear(), displayDate.getMonth() - 1, 1);
            onMonthChange(prevMonthDate);
        }
    };
    const handleNextMonth = () => {
        if (typeof onMonthChange === 'function') {
            const nextMonthDate = new Date(displayDate.getFullYear(), displayDate.getMonth() + 1, 1);
            onMonthChange(nextMonthDate);
        }
    };

    const handleDayClick = (dayInfo) => {
        if (isReadOnly) return;

        if (dayInfo.type === 'empty' || (dayInfo.type === 'past' && !dayInfo.reservationDetails)) {
            return;
        }

        // If it's a reservation and we're NOT currently picking a range, show details
        if (dayInfo.reservationDetails && selectedDates.length === 0 && typeof onShowDetails === 'function') {
            onShowDetails(dayInfo.reservationDetails);
            return;
        }

        // Otherwise, if we have onDateSelect, try to select
        if (typeof onDateSelect === 'function') {
            // Allow clicking available days, blocks, or ANY day if we're extending a range or it's available
            if (dayInfo.type === 'available' || dayInfo.isSelected || dayInfo.blockData || selectedDates.length > 0) {
                if (selectionMode === 'blocks' && dayInfo.blockData) {
                    onDateSelect(dayInfo.blockData);
                } else {
                    onDateSelect(dayInfo.dateString);
                }
            } else if (dayInfo.reservationDetails && typeof onShowDetails === 'function') {
                // Fallback for reserved days if not extending range
                onShowDetails(dayInfo.reservationDetails);
            }
        } else if (dayInfo.reservationDetails && typeof onShowDetails === 'function') {
            onShowDetails(dayInfo.reservationDetails);
        }
    };

    return (
        <div className={styles.calendarContainer}>
            <div className={styles.calendarHeader}>
                <button onClick={handlePrevMonth} aria-label="Previous month" disabled={isLoading}>&lt;</button>
                <h3 id="month-year">{MONTH_NAMES[currentMonth]} {currentYear}</h3>
                <button onClick={handleNextMonth} aria-label="Next month" disabled={isLoading}>&gt;</button>
            </div>

            <div className={styles.calendarWeekdays}>
                {WEEKDAY_NAMES.map(day => <div key={day}>{day}</div>)}
            </div>

            <div className={styles.gridContainer}>
                {isLoading && <div className={styles.loadingOverlay}>Loading Reservations...</div>}
                <div className={styles.calendarDaysGrid} style={{ opacity: isLoading ? 0.6 : 1 }}>
                    {daysInMonthGrid.map(dayInfo => (
                        <div
                            key={dayInfo.key}
                            className={`
                                ${styles.calendarDay}
                                ${
                                /* Logic for Public Reserved colors */
                                dayInfo.type === 'publicReserved' && dayInfo.status === 'pending' ? styles.pending :
                                    dayInfo.type === 'publicReserved' && dayInfo.status === 'approved' ? styles.approved :
                                        styles[dayInfo.type] || styles.available
                                }
                                ${dayInfo.type === 'closure' ? styles.closure : ''}
                                ${dayInfo.isSelected ? styles.selected : ''}
                                ${dayInfo.isBeingViewed ? styles.viewing : ''}
                                ${dayInfo.type === 'currentUserReserved' && !isReadOnly ? styles.clickableReserved : ''}
                                ${(dayInfo.type === 'pending' || dayInfo.type === 'approved' || dayInfo.type === 'rejected' || dayInfo.type === 'cancelled') && typeof onShowDetails === 'function' && !isReadOnly ? styles.clickableReserved : ''}
                                ${dayInfo.type === 'otherReserved' && (!onShowDetails || isReadOnly) ? styles.nonClickableReserved : ''}
                                ${dayInfo.type === 'available' && isReadOnly ? styles.readOnlyAvailable : ''}
                                ${dayInfo.blockData ? styles.isBlock : ''}
                                ${selectionMode === 'blocks' && dayInfo.blockData && hoveredBlockId === dayInfo.blockData.id ? styles.blockHovered : ''}
                            `}
                            data-date={dayInfo.dateString}
                            onClick={() => handleDayClick(dayInfo)}
                            onMouseEnter={() => dayInfo.blockData && setHoveredBlockId(dayInfo.blockData.id)}
                            onMouseLeave={() => setHoveredBlockId(null)}
                            aria-pressed={!isReadOnly && (dayInfo.isSelected || (dayInfo.type !== 'available' && dayInfo.type !== 'past' && dayInfo.type !== 'empty'))}
                            tabIndex={isReadOnly || dayInfo.type === 'empty' || (dayInfo.type === 'past' && !dayInfo.reservationDetails) || (dayInfo.type !== 'available' && !dayInfo.reservationDetails && !onShowDetails && dayInfo.type !== 'closure') ? -1 : 0}
                            role={isReadOnly || (dayInfo.type !== 'available' && !onShowDetails && !onDateSelect) ? 'gridcell' : 'button'}
                            title={dayInfo.type === 'closure' ? (dayInfo.reservationDetails?.text || 'Facility Closed') : ''}
                            aria-label={dayInfo.type !== 'empty' ? `${MONTH_NAMES[currentMonth]} ${dayInfo.dayNumber}, Status: ${dayInfo.reservationDetails?.text || dayInfo.status || dayInfo.type}` : 'Empty cell'}
                        >
                            {dayInfo.type !== 'empty' && (
                                <>
                                    <div className={styles.reservationDetails}>
                                        {dayInfo.type === 'publicReserved' && (
                                            <>
                                                {dayInfo.reservationDetails?.statusLabel && dayInfo.reservationDetails?.churchName ? (
                                                    <div className={styles.publicReservationEvent}>
                                                        <strong style={{ display: 'block' }}>{dayInfo.reservationDetails.statusLabel}</strong>
                                                        <span style={{ fontStyle: 'italic', fontSize: '0.85em', display: 'block' }}>
                                                            {dayInfo.reservationDetails.churchName}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    dayInfo.reservationDetails?.text && <strong>{dayInfo.reservationDetails.text}</strong>
                                                )}
                                            </>
                                        )}
                                        {dayInfo.type === 'currentUserReserved' && !isReadOnly && (
                                            <>
                                                <strong>Your Booking</strong>
                                                <span className={styles.editCancelText}>(Click to View/Cancel)</span>
                                            </>
                                        )}
                                        {dayInfo.type === 'otherReserved' && !isReadOnly && (
                                            <strong>Reserved</strong>
                                        )}
                                        {dayInfo.type !== 'publicReserved' && !isReadOnly && (dayInfo.type === 'pending' || dayInfo.type === 'approved' || dayInfo.type === 'rejected' || dayInfo.type === 'cancelled') && (
                                            <>
                                                {dayInfo.reservationDetails?.displayUsername && dayInfo.reservationDetails.displayUsername !== 'N/A' && (
                                                    <strong className={styles.reservationUser} title={dayInfo.reservationDetails.userEmail || ''}>
                                                        {dayInfo.reservationDetails.displayUsername}
                                                    </strong>
                                                )}
                                                <span className={styles.reservationStatusText}>
                                                    {dayInfo.status ? dayInfo.status.charAt(0).toUpperCase() + dayInfo.status.slice(1) : 'Unknown'}
                                                </span>
                                            </>
                                        )}
                                        {dayInfo.type === 'closure' && (
                                            <>
                                                <strong>{dayInfo.reservationDetails?.text || 'Closed'}</strong>
                                                {dayInfo.reservationDetails?.reason && (
                                                    <span className={styles.closureReason}>
                                                        {dayInfo.reservationDetails.reason}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <span className={styles.dayNumber}>{dayInfo.dayNumber}</span>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Calendar;